import { type ReactNode, useEffect, useRef } from 'react';
import { OverlayProvider } from '@react-aria/overlays';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import type { CustomAppProps } from '@tn/appshell/AppShell/AppShell';
import AppShell from '@tn/appshell/AppShell/AppShell';
import { fetchRemotePageCSSAssetsForRemoteModule } from '@tn/appshell/utils/DocumentHeadAssets';
import type { RemotePageCSSAssetsResult } from '@tn/appshell/utils/getRemotePageCSSAssets';
import { RemotePageCSSHead } from '@tn/appshell/utils/RemotePageCSSHead';
import { configurationConsumer, loadServerConfig } from '@tn/configs';
import RemotePageBoundary from '@tn/foundation/components/ErrorBoundary/RemotePageBoundary';
import TanstackDevTools from '@tn/foundation/components/TanstackDevTools/TanstackDevTools';
import {
    DMAProvider,
    resolveInitialDma,
    resolveInitialStationId,
} from '@tn/foundation/entities/dma/hooks/useDMA';
import { prefetchDmaSSR, resolveSsrDma } from '@tn/foundation/entities/dma/hooks/useDmas';
import type { Coordinates } from '@tn/foundation/entities/location/utils/configs/location';
import useBrowserAgent from '@tn/foundation/hooks/new-relic/useBrowserAgent';
import useFullUser, {
    buildFullUserProfile,
    prefetchFullUser,
} from '@tn/foundation/hooks/providers/useFullUser';
import { ZoneMapProvider } from '@tn/foundation/hooks/routes/useZone';
import useInitializeGlobalQueryClient from '@tn/foundation/hooks/utils/useInitializeGlobalQueryClient';
import { appLogger, registerLoggerBackend } from '@tn/foundation/utils/app-logger/appLogger';
import { createNewRelicLoggerBackend } from '@tn/foundation/utils/app-logger/backends/newRelicBackend';
import createLayout from '@tn/foundation/utils/createLayout';
import { getIpAddressFromRequest } from '@tn/foundation/utils/devtools/ip-utils';
import { setCurrentEnvOnWindow } from '@tn/foundation/utils/getCurrentEnv';
import { clearAuthAndStatusCookiesIfSessionGone } from '@tn/foundation/utils/server/clear-auth-and-status-cookies';
import { getLOB } from '@tn/foundation/utils/server/lob-utils';
import { handleMfeNotFound } from '@tn/foundation/utils/server/notFoundResponse';
import { checkIsCSR, isStaticAsset, ssrState } from '@tn/foundation/utils/server/prefetch-utils';
import { getRegion } from '@tn/foundation/utils/server/region-utils';
import { resolveSsrViewportWidth } from '@tn/foundation/utils/server/resolveSsrViewportWidth';
import { getTealiumEnv } from '@tn/foundation/utils/tracking/getTealiumEnv';
import { loadTealiumClient } from '@tn/foundation/utils/tracking/loadTealiumClient';
import { ThemeWrapper } from '@vds/components';
import type { NextComponentType } from 'next';
import App, { AppContext } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';

import SetDeviceIdCookie from '@/components/SetDeviceIdCookie';
import pageRemoteMap from '@/config/page-remote-map.json';
import zoneMap from '@/config/zone-map.json';

import '@vds/components/dist/resets.css';
import '@vds/components/dist/main.css';
import '../styles/globals.css';
import '../styles/ssr-carousel.scss';

// Extend AppContext to add 'remoteModule' to Component
type ExtendedAppContext = Omit<AppContext, 'Component'> & {
    Component: NextComponentType & { remoteModule?: string; remoteModules?: string[] };
};

// Rendered inside QueryClientProvider so useFullUser() can call useQueryClient() safely.
function DMALayer({
    ssrHasUser,
    dmaDisabled = false,
    locationEnabled = true,
    children,
    initialDma,
    initialStationId,
    initialCoordinates,
}: Readonly<{
    ssrHasUser: boolean;
    dmaDisabled?: boolean;
    locationEnabled?: boolean;
    children: ReactNode;
    initialDma?: number;
    initialStationId?: string;
    initialCoordinates?: Coordinates;
}>) {
    const { isLoggedIn, isFetched } = useFullUser();
    // Until the query resolves, trust the SSR snapshot. Once fetched, let the live value
    // take over so logout/session-expiry is reflected without waiting for a navigation.
    const enabled =
        !dmaDisabled && locationEnabled && (isFetched ? Boolean(isLoggedIn) : ssrHasUser);
    return (
        <DMAProvider
            enabled={enabled}
            initialDma={initialDma}
            initialStationId={initialStationId}
            initialCoordinates={initialCoordinates}>
            {children}
        </DMAProvider>
    );
}
export default function MfeApp({
    Component,
    pageProps,
    region,
    tealiumEnv,
    currentEnv,
    newRelicBrowserAppId,
    trackingData,
    ssrViewportWidth,
    remoteMfe,
    remotePageCSSAssets,
    ssrHasUser = false,
    ssrLocationEnabled = true,
    ssrDma,
    ssrStationId,
    ssrCoordinates,
}: CustomAppProps & {
    remotePageCSSAssets?: RemotePageCSSAssetsResult | null;
    ssrHasUser?: boolean;
    ssrLocationEnabled?: boolean;
    ssrDma?: number;
    ssrStationId?: string;
    ssrCoordinates?: Coordinates;
}) {
    if (typeof window !== 'undefined' && !window.isMfe) {
        window.isMfe = true;
        window.appRegion = region;
        window.tealiumEnv = tealiumEnv;
        if (currentEnv) {
            setCurrentEnvOnWindow(currentEnv);
        }
    }

    useEffect(() => {
        // Tealium utag.js sets properties on these; ensure they exist before the script runs
        const browserGlobal = globalThis as typeof globalThis & Window;
        browserGlobal.utag_data ??= {};
        browserGlobal.utag_cfg_ovrd ??= {};
        loadTealiumClient();
        // region/tealiumEnv/currentEnv are static per session from getInitialProps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const stableTrackingData = useRef(trackingData).current;

    // Create a client
    const [queryClient] = useInitializeGlobalQueryClient();
    useBrowserAgent({ applicationID: newRelicBrowserAppId });

    useEffect(() => {
        registerLoggerBackend(createNewRelicLoggerBackend());
    }, []);

    // Load Components Layout if exposed. If not wrap with theme wrapper.
    const getLayout = Component?.getLayout ?? createLayout({ theme: pageProps.VDSTheme ?? 'dark' });
    const router = useRouter();
    const remoteModule = (Component as { remoteModule?: string })?.remoteModule;

    return (
        <ZoneMapProvider zoneMap={zoneMap}>
            <OverlayProvider>
                <QueryClientProvider client={queryClient}>
                    <Head>
                        <meta
                            key="viewport"
                            name="viewport"
                            content="width=device-width, initial-scale=1"
                        />
                        {remoteMfe && <meta property="tn:mfe-remote" content={remoteMfe} />}
                    </Head>
                    <RemotePageCSSHead remotePageCSSAssets={remotePageCSSAssets ?? null} />
                    <ThemeWrapper
                        target="body"
                        theme={pageProps.VDSTheme ?? 'dark'}
                        useContainer={false}>
                        <HydrationBoundary state={pageProps.dehydratedState}>
                            {getLayout(
                                <DMALayer
                                    ssrHasUser={ssrHasUser}
                                    dmaDisabled={
                                        !(Component as { dmaEnabled?: boolean }).dmaEnabled
                                    }
                                    locationEnabled={ssrLocationEnabled}
                                    initialDma={resolveInitialDma(ssrDma, pageProps)}
                                    initialStationId={resolveInitialStationId(
                                        ssrStationId,
                                        pageProps,
                                    )}
                                    initialCoordinates={ssrCoordinates}>
                                    <AppShell
                                        trackingData={stableTrackingData}
                                        appName={pageProps.appName ?? 'app-shell'}
                                        ssrViewportWidth={ssrViewportWidth}
                                        remoteMfe={remoteMfe}
                                        clientUserGuid={pageProps.clientUserGuid}
                                        abTestsExtraParams={pageProps.abTestsExtraParams}>
                                        <RemotePageBoundary
                                            remoteModule={remoteModule}
                                            remoteUnavailable={
                                                (pageProps as { __remoteUnavailable?: boolean })
                                                    ?.__remoteUnavailable
                                            }
                                            resetKey={router.asPath}>
                                            <Component {...pageProps} />
                                        </RemotePageBoundary>
                                    </AppShell>
                                </DMALayer>,
                            )}
                        </HydrationBoundary>
                        <SetDeviceIdCookie />
                        <TanstackDevTools />
                    </ThemeWrapper>
                </QueryClientProvider>
            </OverlayProvider>
        </ZoneMapProvider>
    );
}

MfeApp.getInitialProps = async (appContext: ExtendedAppContext) => {
    const appProps = await App.getInitialProps(appContext);
    const { req, res } = appContext.ctx;

    // Asset 404s: plain-text uncacheable 404 (all brands). Page 404s: legacy redirect to
    // the P+ error page on P+ only — Pluto falls through to the regular 404 render. The
    // response is ended either way, so the remaining setup is skipped.
    if (handleMfeNotFound(req, res)) {
        return appProps;
    }

    const isServer = typeof window === 'undefined';
    const region = getRegion(req);
    const lob = getLOB(req);
    let ssrHasUser = false;
    let ssrLocationEnabled = true;
    let ssrDma: number | undefined;
    let ssrStationId: string | undefined;
    let ssrCoordinates: Coordinates | undefined;
    if (isServer && req && !checkIsCSR(req) && !isStaticAsset(req.url ?? '')) {
        try {
            const state = ssrState(req);
            const [userResp] = await state.fetchSSR([prefetchFullUser]);
            clearAuthAndStatusCookiesIfSessionGone({ req, res, userResp });
            const { userFullProfile } = buildFullUserProfile(userResp ?? undefined);
            ssrHasUser = Boolean(userFullProfile?.isLoggedIn);
            const pageDmaEnabled = !!(appContext.Component as { dmaEnabled?: boolean }).dmaEnabled;
            if (pageDmaEnabled) {
                ssrLocationEnabled =
                    ((await configurationConsumer.getConfigByKey({
                        key: 'options.locationEnabled',
                        region,
                        lob,
                    })) as boolean) ?? true;
            }
            if (ssrHasUser && pageDmaEnabled) {
                const dmaEnabled =
                    ((await configurationConsumer.getConfigByKey({
                        key: 'options.dmaEnabled',
                        region,
                        lob,
                    })) as boolean) ?? false;
                const ssrResult = await prefetchDmaSSR(state, userFullProfile, {
                    query: appContext.ctx.query,
                    locationEnabled: ssrLocationEnabled,
                });
                if (ssrResult !== undefined) {
                    ssrDma = resolveSsrDma(ssrResult, dmaEnabled);
                    ssrStationId = ssrResult.dmaResp?.dmaInfo?.dmas?.[0]?.stationId ?? undefined;
                    ssrCoordinates = ssrResult.coords ?? undefined;
                }
            }
        } catch (error) {
            appLogger.warn(
                'DMA SSR prefetch failed in app-shell getInitialProps — falling back to client-side',
                {
                    region,
                    lob,
                    errorMessage: error instanceof Error ? error.message : String(error),
                },
            );
        }
    }

    const tealiumEnv = await getTealiumEnv(region, lob);

    // Get NEWRELIC_BROWSER_APPID and trackingSiteName from jsonc configuration
    let newRelicBrowserAppId: string | undefined;
    let trackingSiteName: string | undefined;
    if (isServer && req && !checkIsCSR(req) && !isStaticAsset(req.url ?? '')) {
        const serverConfig = await loadServerConfig(region, lob, {
            newRelicBrowserAppId: 'env.NEWRELIC_BROWSER_APPID',
            siteName: 'options.trackingConfig.siteName',
        });
        newRelicBrowserAppId = serverConfig.newRelicBrowserAppId as string | undefined;
        trackingSiteName = serverConfig.siteName as string | undefined;
    }
    const deviceIp = getIpAddressFromRequest(req);
    const trackingData = { deviceIp, siteName: trackingSiteName };

    const remotePageCSSAssets = await fetchRemotePageCSSAssetsForRemoteModule(
        appContext.Component.remoteModules ?? appContext.Component.remoteModule,
    );
    const pathname = appContext.ctx.pathname ?? '';
    const remoteMfesFromMap = (pageRemoteMap as Record<string, string[]>)[pathname];
    const remoteMfe = remoteMfesFromMap?.[0] ?? appContext.Component.remoteModule?.split('/')[0];

    const ssrViewportWidth = resolveSsrViewportWidth(req, { revealWhenUnknown: true });

    // Get CURRENT_ENV from server-side (only available on server)
    const currentEnv =
        typeof process !== 'undefined' && process.env?.CURRENT_ENV
            ? process.env.CURRENT_ENV
            : undefined;

    return {
        ...appProps,
        ...{
            region,
            tealiumEnv,
            currentEnv,
            newRelicBrowserAppId,
            trackingData,
            ssrViewportWidth,
            remoteMfe,
            remotePageCSSAssets,
            ssrHasUser,
            ssrLocationEnabled,
            ssrDma,
            ssrStationId,
            ssrCoordinates,
        },
    };
};
